import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as orgRepo from "@/stores/organizations/repository";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";

interface ClearExpiredInvitesParams {
  inviteIds: number[];
}

/**
 * Cancel every expired organization invitation in one go — backs the
 * "Clear all expired" action on the Expired group header.
 *
 * Resolves with the IDs actually cancelled — RLS can drop some, in which case the
 * rest are still cancelled for real. Success/partial/error toasts are surfaced by
 * the caller so they can be localized; this hook only performs the mutation and
 * refreshes the org invites list.
 */
export const useClearExpiredInvites = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizationContext();

  return useMutation({
    mutationFn: async ({ inviteIds }: ClearExpiredInvitesParams) => {
      return orgRepo.cancelInvites(inviteIds);
    },
    // Refetch on failure too: the throw only covers "nothing was cancelled", but a
    // stale Expired list after any outcome would let the user re-cancel dead ids.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-invites", currentOrganization?.id],
      });
    },
  });
};
